/**
 * Parsing a list view's own state back out of the URL.
 *
 * Every list surface (recipes, batches, inventory, ingredients) is putting
 * its search box, filter chips, and sort control into `?query=…` so that
 * going back to a list restores what the brewer was looking at. All of them
 * face the same problem: a `validateSearch` function receives `unknown` —
 * whatever the address bar happened to contain, including nothing at all,
 * whitespace, a number typed where a string belongs, or the array a repeated
 * key produces — and has to turn that into a typed value without throwing.
 * These parsers are that boundary, built once so every route's own
 * `validateSearch` can stay a one-line call.
 *
 * `undefined`, never `null`, marks "absent" throughout. TanStack Router's
 * default search serializer stringifies every key a `validateSearch`
 * returns — `null` included — so a route that echoed `null` back would print
 * a literal `?tag=null` in the address bar on a plain visit with no `tag` at
 * all. `undefined` is the one value the router's `encode` drops, which is
 * the difference between a clean URL and a permanent wart (see
 * `apps/web/src/routes/ingredients.tsx`, where this was found the hard way).
 * The same reasoning applies to the list parser: an empty result is
 * `undefined`, not `[]`, so `?tag=` or a query string that named nothing
 * real also vanishes from the URL instead of persisting as an empty param.
 */

/**
 * A trimmed, non-empty string, or `undefined`.
 *
 * For a free-text search box: `?query=` a brewer typed by hand, or one a
 * previous page wrote back out, can be missing, blank, whitespace-only, the
 * array a repeated key produces, or (least likely but still just `unknown`)
 * a number or object. All of those mean "no search", so they collapse to the
 * one falsy value every caller already checks for.
 */
export function parseSearchString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * `value` if it is one of `allowed`, else `undefined`.
 *
 * For a sort key or a single-choice filter chip. Typed so the return is the
 * union `allowed` was declared with, not `string` — a caller that already
 * knows its own sort keys should not have to re-narrow what this function
 * just checked. `allowed` is read as a plain array of the same union, so a
 * route passes its own `as const` tuple (e.g. `BATCH_SORTS`) straight
 * through.
 */
export function parseSearchEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/**
 * A de-duplicated, non-empty list of trimmed strings from a repeated param,
 * or `undefined`.
 *
 * A repeated key like `?tag=a&tag=b` arrives from the router as an array;
 * `?tag=a` alone arrives as a bare string. Both are legitimate shapes for
 * the same param — the difference is only how many times it appeared — so
 * both are accepted here rather than making every caller normalize them
 * first. Duplicates are removed (two identical filter chips are one filter,
 * not a reason to render two), empty entries are dropped (a stray `?tag=`
 * in the middle of the list), and an empty result is `undefined` rather
 * than `[]` so a filter that named nothing real leaves the URL clean too.
 */
export function parseSearchList(value: unknown): readonly string[] | undefined {
  const parts = Array.isArray(value) ? value : [value];
  const cleaned = parts
    .filter((part): part is string => typeof part === 'string')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  const deduped = [...new Set(cleaned)];
  return deduped.length === 0 ? undefined : deduped;
}

/**
 * `true` if the flag was set, or `undefined` — never `false`.
 *
 * For an on/off filter (an "include archived" toggle, a "show mine only"
 * checkbox): the off state is the default, so — the same reasoning as every
 * parser above — it is not worth a character in the address bar. That is
 * also why this returns `true | undefined` rather than `boolean | undefined`:
 * there is no third state to represent, so the type says so, and a caller
 * reads the flag with `?? false` exactly once, at the one place it needs an
 * actual `boolean`.
 *
 * `value` may already be the boolean `true` — TanStack Router's default
 * search parser runs `JSON.parse` on every value, so a URL this app wrote
 * itself (`?archived=true`) decodes straight back to one — or it may be the
 * *string* `'true'`, which is what a hand-typed link, an older bookmark, or a
 * parser that left the value alone produces instead. Anything else —
 * `'1'`, `'yes'`, the string `'false'`, a stray array — is not a spelling of
 * "on" this function is willing to guess at, and falls back to `undefined`.
 */
export function parseSearchFlag(value: unknown): true | undefined {
  return value === true || value === 'true' ? true : undefined;
}

// biome-ignore lint/suspicious/noControlCharactersInRegex: matching them is the point.
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;
const ENCODED_CONTROL_CHARACTER = /%(?:[01][0-9a-f]|7f)/i;

/**
 * A path on this origin to go back to, or `undefined`.
 *
 * For `/signin?next=`, which hands its value to the auth server as the place
 * a magic link or Google lands. That makes it an open-redirect door if it is
 * taken as given, so only a same-origin absolute path passes: it starts with
 * one `/`, and not `//` or `/\`, which a browser reads as another host, and
 * carries no control character that a browser would strip into one.
 */
export function parseReturnPath(value: unknown): string | undefined {
  const path = parseSearchString(value);
  if (path === undefined || !path.startsWith('/')) return undefined;
  if (path.startsWith('//') || path.startsWith('/\\')) return undefined;
  // A control character, raw or percent-encoded: a browser strips a tab or a
  // newline out of a URL before it reads it, so `/\t/evil.example` would go
  // through the check above and come out the other side as `//evil.example`.
  if (CONTROL_CHARACTER.test(path) || ENCODED_CONTROL_CHARACTER.test(path)) return undefined;
  return path;
}
